import React from "react";
import { Row, Col } from "reactstrap";
import {
    CURRENCY_SYMBOL,
    formatCurrencyCode,
    formatFxRate,
    getFxRate,
    summarizeBalancesInTry,
    toTry,
} from "../../utils/currencyUtils";

const NAVY = "#221C5D";
const GREEN = "#71C30B";
const RED = "#dc3545";
// Uygulama tema turuncusu (--color-orange ile uyumlu) — hero kartı için.
const ORANGE = "#E56A22";
const ORANGE_DARK = "#D9772E";

/** Kart avatarlarının para birimi bazlı vurgu renkleri. */
const CURRENCY_ACCENT = {
    TRY: { bg: "#EEF3FB", color: NAVY },
    USD: { bg: "#EAF7EE", color: "#1E8E4A" },
    EUR: { bg: "#FDF3E2", color: "#C97C1B" },
    GBP: { bg: "#FCECEC", color: "#C0453F" },
};

const accentOf = (currency) =>
    CURRENCY_ACCENT[(currency || "").toUpperCase()] || { bg: "#F1F3F7", color: "#6c757d" };

const balanceColor = (value) => {
    const num = Number(value || 0);
    if (num === 0) return "#1e293b";
    return num > 0 ? GREEN : RED;
};

const heroStyles = `
  .currency-summary-hero {
    background: linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 100%);
    position: relative;
    overflow: hidden;
  }
  .currency-summary-hero::after {
    content: '';
    position: absolute;
    top: -55%;
    right: -6%;
    width: 320px;
    height: 320px;
    border-radius: 50%;
    background: rgba(255, 255, 255, .05);
    pointer-events: none;
  }
  .currency-summary-hero > * { position: relative; z-index: 1; }
  .currency-summary-hero .hero-label {
    letter-spacing: .08em;
    text-transform: uppercase;
    font-size: .72rem;
    font-weight: 600;
    color: rgba(255, 255, 255, .65);
  }
  .currency-summary-hero .hero-amount {
    font-size: clamp(1.6rem, 4.5vw, 2.4rem);
    font-weight: 700;
    color: #fff;
    line-height: 1.15;
    word-break: break-word;
  }
  .currency-summary-hero .hero-meta { color: rgba(255, 255, 255, .72); font-size: .85rem; }
  .currency-summary-hero .hero-pill {
    border-radius: 999px;
    padding: .3rem .85rem;
    font-size: .8rem;
    font-weight: 600;
    border: 1px solid rgba(255, 255, 255, .18);
    background: rgba(255, 255, 255, .08);
    color: rgba(255, 255, 255, .9);
    white-space: nowrap;
  }
  .currency-summary-hero .hero-pill.positive { border-color: rgba(113, 195, 11, .45); color: #C8E99A; }
  .currency-summary-hero .hero-pill.negative { border-color: rgba(161, 22, 33, .45); background: rgba(255, 255, 255, .22); color: #A11621; }
`;

const cardStyles = `
  .currency-balance-card { background: #f7f8fa; transition: box-shadow .2s ease; }
  .currency-balance-card:hover { box-shadow: 0 .35rem .9rem rgba(10, 49, 110, .08); }
  .currency-balance-card .currency-avatar {
    width: 38px; height: 38px; min-width: 38px;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 1.05rem;
  }
  .currency-balance-card .currency-amount { font-size: 1.35rem; font-weight: 700; word-break: break-word; }
  .currency-balance-card .rate-chip {
    display: inline-block;
    background: #e9ecef;
    color: #6c757d;
    border-radius: 6px;
    padding: .15rem .5rem;
    font-size: .75rem;
  }
`;

/**
 * Referans tasarımdaki üst özet kartı: TL karşılığı net toplam bakiye,
 * TL bazında borç/tahsilat ve her döviz için özet etiketleri.
 */
export const NetBalanceHero = ({ balances, finalRates, className = "" }) => {
    const list = Array.isArray(balances) ? balances : [];
    if (list.length === 0) return null;

    const { hasConverted, netBalance, totalDebt, totalReceipt, unconvertedCurrencies } =
        summarizeBalancesInTry(list, finalRates);

    return (
        <div className={`currency-summary-hero rounded-4 shadow-lg p-4 ${className}`}>
            <style>{heroStyles}</style>

            <div className="hero-label mb-1">Net Toplam Bakiye (TL Karşılığı)</div>
            <div className="hero-amount mb-2">
                {hasConverted ? formatCurrencyCode(netBalance, "TRY") : "-"}
            </div>

            {hasConverted && (
                <div className="hero-meta mb-3">
                    Borç: {formatCurrencyCode(totalDebt, "TRY")}
                    <span className="mx-2">·</span>
                    Tahsilat: {formatCurrencyCode(totalReceipt, "TRY")}
                </div>
            )}

            <div className="d-flex flex-wrap gap-2">
                {list.map((b) => {
                    const code = (b.currency || "TRY").toUpperCase();
                    const tryValue = toTry(b.currentBalance, code, finalRates);
                    const isLocal = code === "TRY";
                    const shown = tryValue == null ? Number(b.currentBalance || 0) : tryValue;
                    const tone = shown === 0 ? "" : shown > 0 ? "positive" : "negative";

                    return (
                        <span key={code} className={`hero-pill ${tone}`}>
                            {code}
                            {isLocal || tryValue == null ? " " : " → "}
                            {tryValue == null
                                ? formatCurrencyCode(b.currentBalance, code)
                                : formatCurrencyCode(tryValue, "TRY")}
                        </span>
                    );
                })}
            </div>

            {unconvertedCurrencies.length > 0 && (
                <div className="hero-meta mt-3" style={{ fontSize: ".78rem" }}>
                    <i className="bx bx-info-circle me-1" />
                    Kuru tanımlı olmayan para birimleri ({unconvertedCurrencies.join(", ")}) TL toplamına dahil
                    edilmedi.
                </div>
            )}
        </div>
    );
};

/**
 * Her döviz için orijinal bakiye, TL karşılığı, güncel kur ve borç/tahsilat kırılımı.
 */
export const CurrencyBalanceCards = ({ balances, finalRates }) => {
    const list = Array.isArray(balances) ? balances : [];
    if (list.length === 0) return null;

    return (
        <>
            <style>{cardStyles}</style>
            <div className="d-flex align-items-center gap-2 mb-3">
                <i className="bx bx-wallet" style={{ color: NAVY, fontSize: "1.1rem" }} />
                <h6 className="mb-0 fw-bold text-uppercase" style={{ letterSpacing: ".04em", color: "#475569" }}>
                    Bakiyeler — Orijinal Döviz
                </h6>
            </div>

            <Row className="g-3">
                {list.map((b) => {
                    const code = (b.currency || "TRY").toUpperCase();
                    const accent = accentOf(code);
                    const isLocal = code === "TRY";
                    const rate = getFxRate(code, finalRates);
                    const tryValue = toTry(b.currentBalance, code, finalRates);

                    return (
                        <Col md={6} key={code}>
                            <div className="currency-balance-card border rounded-4 p-3 h-100">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <span
                                        className="currency-avatar"
                                        style={{ background: accent.bg, color: accent.color }}
                                    >
                                        {CURRENCY_SYMBOL[code] || code}
                                    </span>
                                    <div>
                                        <div className="fw-bold text-dark">{code}</div>
                                        {isLocal ? (
                                            <small className="fw-semibold" style={{ color: NAVY }}>
                                                Yerel Para
                                            </small>
                                        ) : (
                                            <span
                                                className="badge rounded-pill bg-white border text-muted fw-normal"
                                                style={{ fontSize: ".7rem" }}
                                            >
                                                Döviz
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div
                                    className="currency-amount mb-1"
                                    style={{ color: balanceColor(b.currentBalance) }}
                                >
                                    {formatCurrencyCode(b.currentBalance, code)}
                                </div>

                                {!isLocal && (
                                    <div className="mb-2">
                                        {tryValue != null ? (
                                            <>
                                                <div className="text-muted small mb-1">
                                                    ≈ {formatCurrencyCode(tryValue, "TRY")} TL karşılığı
                                                </div>
                                                <span className="rate-chip">{formatFxRate(code, rate)}</span>
                                            </>
                                        ) : (
                                            <div className="text-muted small">
                                                Güncel kur tanımlı değil, TL karşılığı hesaplanamıyor.
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="d-flex flex-wrap justify-content-between gap-2 small text-muted pt-2 border-top">
                                    <span>Borç: {formatCurrencyCode(b.totalDebtAmount || 0, code)}</span>
                                    <span>Tahsilat: {formatCurrencyCode(b.totalReceiptAmount || 0, code)}</span>
                                </div>
                            </div>
                        </Col>
                    );
                })}
            </Row>
        </>
    );
};
